import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Typography, Table, Tag, message, Spin, Button, Space, Modal, Form, Input, Switch, Dropdown, Select, Divider, Segmented, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, MoreOutlined, SafetyOutlined, LaptopOutlined, MailOutlined, SendOutlined, SearchOutlined, SafetyCertificateTwoTone } from '@ant-design/icons';
import dayjs from 'dayjs';
import NavigationBar from '../../components/NavigationBar';
import DOTheme from '../../components/directOrders/doTheme';
import { useAuth } from '../../context/AuthContext';
import employeeService from '../../services/employeeService';
import laptopService from '../../services/laptopService';
import { API_ORIGIN } from '../../config';
import '../../styles/employees.css';

const { Text } = Typography;

const EmployeeAdmin = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addEmployeeLoading, setAddEmployeeLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editEmployeeLoading, setEditEmployeeLoading] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteEmployeeData, setDeleteEmployeeData] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1); // 1 for first confirmation, 2 for second
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // Role management state
  const [activeTab, setActiveTab] = useState('employees');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleForm] = Form.useForm();

  // Additional roles state
  const [editingUserAdditionalRoles, setEditingUserAdditionalRoles] = useState([]);
  const [additionalRoleToAdd, setAdditionalRoleToAdd] = useState(null);
  const [addingAdditionalRole, setAddingAdditionalRole] = useState(false);

  // Laptop inventory state
  const [laptops, setLaptops] = useState([]);
  const [laptopLoading, setLaptopLoading] = useState(false);
  const [laptopSearch, setLaptopSearch] = useState('');
  const [laptopModalVisible, setLaptopModalVisible] = useState(false);
  const [laptopSaving, setLaptopSaving] = useState(false);
  const [editingLaptop, setEditingLaptop] = useState(null);
  const [laptopDeleteData, setLaptopDeleteData] = useState(null);
  const [laptopDeleteVisible, setLaptopDeleteVisible] = useState(false);
  const [laptopForm] = Form.useForm();
  // Directory (people view)
  const [selectedKey, setSelectedKey] = useState(null);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peopleFilter, setPeopleFilter] = useState('all'); // 'all' | 'waiting' | 'nolaptop'
  const [laptopSendingId, setLaptopSendingId] = useState(null); // laptopId currently sending a single confirmation
  const [laptopSendAllConfirmVisible, setLaptopSendAllConfirmVisible] = useState(false);
  const [laptopSendAllLoading, setLaptopSendAllLoading] = useState(false);
  const [laptopSendAllResult, setLaptopSendAllResult] = useState(null);
  const [laptopSendAllResultVisible, setLaptopSendAllResultVisible] = useState(false);

  // Fetch all employees from API
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/employee/allEmployees`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Handle different response formats
      if (data.success && data.employees) {
        setEmployees(data.employees);
      } else if (Array.isArray(data)) {
        setEmployees(data);
      } else {
        setEmployees([]);
      }
      
    } catch (error) {
      message.error('Failed to load employees: ' + error.message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Load employees on component mount
  useEffect(() => {
    fetchEmployees();
    fetchUsers();
    fetchRoles();
    fetchLaptops();
  }, []);

  // Fetch all users with roles
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await employeeService.getAllUsers();
      if (data.success && data.users) {
        setUsers(data.users);
      }
    } catch (error) {
      message.error('Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all available roles
  const fetchRoles = async () => {
    try {
      const data = await employeeService.getAllRoles();
      if (data.success && data.roles) {
        setRoles(data.roles);
      }
    } catch (error) {
      message.error('Failed to load roles: ' + error.message);
    }
  };

  // Handle editing user role
  const handleEditRole = async (userRecord) => {
    setEditingUser(userRecord);
    roleForm.setFieldsValue({
      role: userRecord.role
    });
    setEditingUserAdditionalRoles([]);
    setAdditionalRoleToAdd(null);
    setRoleModalVisible(true);
    try {
      const data = await employeeService.getAdditionalRoles(userRecord.userId);
      setEditingUserAdditionalRoles(data.additionalRoles || []);
    } catch (error) {
      // silent fail for additional roles
    }
  };

  // Refresh additional roles from API
  const refreshAdditionalRoles = async (userId) => {
    try {
      const data = await employeeService.getAdditionalRoles(userId);
      setEditingUserAdditionalRoles(data.additionalRoles || []);
    } catch (error) {
      // silent fail for additional roles
    }
  };

  // Handle adding an additional role
  const handleAddAdditionalRole = async () => {
    if (!additionalRoleToAdd || !editingUser) return;
    setAddingAdditionalRole(true);
    try {
      await employeeService.addAdditionalRole(editingUser.userId, additionalRoleToAdd);
      setAdditionalRoleToAdd(null);
      message.success('Additional role added');
      await refreshAdditionalRoles(editingUser.userId);
      fetchUsers();
    } catch (error) {
      message.error('Failed to add role: ' + error.message);
    } finally {
      setAddingAdditionalRole(false);
    }
  };

  // Handle removing an additional role
  const handleRemoveAdditionalRole = async (role) => {
    try {
      await employeeService.removeAdditionalRole(editingUser.userId, role);
      message.success('Additional role removed');
      await refreshAdditionalRoles(editingUser.userId);
      fetchUsers();
    } catch (error) {
      message.error('Failed to remove role: ' + error.message);
    }
  };

  // Handle updating user role
  const handleUpdateRole = async (values) => {
    if (!editingUser) return;
    
    setRoleLoading(true);
    try {
      const data = await employeeService.updateUserRole(editingUser.userId, values.role);
      if (data.success) {
        message.success('User role updated successfully!');
        setRoleModalVisible(false);
        roleForm.resetFields();
        setEditingUser(null);
        fetchUsers();
      }
    } catch (error) {
      message.error('Failed to update role: ' + error.message);
    } finally {
      setRoleLoading(false);
    }
  };

  // Get role name by role ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.role === roleId);
    return role ? role.name : 'Unknown';
  };

  // Handle adding a new employee
  const handleAddEmployee = async (values) => {
    setAddEmployeeLoading(true);
    try {
      const employeeData = {
        fName: values.fName,
        lName: values.lName,
        empEmail: values.empEmail,
        DisturbanceAllowance: values.DisturbanceAllowance ? 1 : 0, // Convert boolean to tinyint
        hourlyRate : values.hourlyRate || '0',
        grossWage : values.grossWage || '0'
      };

      const response = await fetch(`${API_ORIGIN}/api/employee/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(employeeData)
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        // Get the response text first
        const responseText = await response.text();
        
        try {
          // Try to parse as JSON
          const errorData = JSON.parse(responseText);
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (jsonError) {
          
          // Look for common error patterns in the response
          if (responseText.includes('email already exists')) {
            errorMessage = 'An employee with this email already exists';
          } else if (responseText.includes('error')) {
            // Try to extract error message using regex
            const errorMatch = responseText.match(/"error":"([^"]+)"/);
            if (errorMatch && errorMatch[1]) {
              errorMessage = errorMatch[1];
            } else if (responseText) {
              errorMessage = responseText;
            }
          } else if (responseText) {
            errorMessage = responseText;
          }
        }
        
        throw new Error(errorMessage);
      }

      message.success('Employee added successfully!');
      
      // Close modal and reset form
      setAddModalVisible(false);
      form.resetFields();
      
      // Refresh the employee list
      fetchEmployees();
      
    } catch (error) {
      
      // Check if it's an email already exists error
      if (error.message.includes('email already exists')) {
        // Set field error for email
        form.setFields([
          {
            name: 'empEmail',
            errors: [error.message]
          }
        ]);
      } else {
        // For other errors, show general message
        message.error('Failed to add employee: ' + error.message);
      }
    } finally {
      setAddEmployeeLoading(false);
    }
  };

  // Handle canceling the add employee modal
  const handleCancelAdd = () => {
    setAddModalVisible(false);
    form.resetFields();
  };

  // Handle opening edit modal
  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEditModalVisible(true);
    // Pre-populate the form with employee data
    editForm.setFieldsValue({
      fName: employee.fName,
      lName: employee.lName,
      empEmail: employee.empEmail,
      DisturbanceAllowance: employee.DisturbanceAllowance === 1 || employee.DisturbanceAllowance === '1' || employee.DisturbanceAllowance === true,
      hourlyRate: employee.hourlyRate || '0',
      grossWage: employee.grossWage || '0'
    });
  };

  // Handle updating an employee
  const handleUpdateEmployee = async (values) => {
    setEditEmployeeLoading(true);
    try {
      const employeeData = {
        fName: values.fName,
        lName: values.lName,
        empEmail: values.empEmail,
        DisturbanceAllowance: values.DisturbanceAllowance ? 1 : 0, // Convert boolean to tinyint
        hourlyRate : values.hourlyRate || '0',
        grossWage : values.grossWage || '0'
      };


      const response = await fetch(`${API_ORIGIN}/api/employee/update/${editingEmployee.empId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(employeeData)
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        // Get the response text first
        const responseText = await response.text();
        
        try {
          // Try to parse as JSON
          const errorData = JSON.parse(responseText);
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (jsonError) {
          
          // Look for common error patterns in the response
          if (responseText.includes('email already exists')) {
            errorMessage = 'An employee with this email already exists';
          } else if (responseText.includes('error')) {
            // Try to extract error message using regex
            const errorMatch = responseText.match(/"error":"([^"]+)"/);
            if (errorMatch && errorMatch[1]) {
              errorMessage = errorMatch[1];
            } else if (responseText) {
              errorMessage = responseText;
            }
          } else if (responseText) {
            errorMessage = responseText;
          }
        }
        
        throw new Error(errorMessage);
      }

      message.success('Employee updated successfully!');
      
      // Close modal and reset form
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingEmployee(null);
      
      // Refresh the employee list
      fetchEmployees();
      
    } catch (error) {
      
      // Check if it's an email already exists error
      if (error.message.includes('email already exists')) {
        // Set field error for email
        editForm.setFields([
          {
            name: 'empEmail',
            errors: [error.message]
          }
        ]);
      } else {
        // For other errors, show general message
        message.error('Failed to update employee: ' + error.message);
      }
    } finally {
      setEditEmployeeLoading(false);
    }
  };

  // Handle canceling the edit employee modal
  const handleCancelEdit = () => {
    setEditModalVisible(false);
    editForm.resetFields();
    setEditingEmployee(null);
  };

  // Handle deleting an employee with double confirmation
  const handleDeleteEmployee = (employee) => {
    setDeleteEmployeeData(employee);
    setDeleteStep(1);
    setDeleteModalVisible(true);
  };

  // Handle first confirmation OK
  const handleFirstConfirmationOk = () => {
    setDeleteStep(2);
  };

  // Handle second confirmation OK
  const handleSecondConfirmationOk = () => {
    setDeleteModalVisible(false);
    performDeleteEmployee(deleteEmployeeData);
  };

  // Handle delete modal cancel
  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeleteEmployeeData(null);
    setDeleteStep(1);
  };

  // Perform the actual deletion
  const performDeleteEmployee = async (employee) => {
    try {

      const response = await fetch(`${API_ORIGIN}/api/employee/delete/${employee.empId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        // Get the response text first
        const responseText = await response.text();
        
        try {
          // Try to parse as JSON
          const errorData = JSON.parse(responseText);
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (jsonError) {
          if (responseText) {
            errorMessage = responseText;
          }
        }
        
        throw new Error(errorMessage);
      }
      
      message.success(`Employee ${employee.fName} ${employee.lName} deleted successfully!`);
      
      // Refresh the employee list
      fetchEmployees();
      
    } catch (error) {
      message.error('Failed to delete employee: ' + error.message);
    }
  };

  // ----- Laptop inventory handlers -----

  // Fetch all laptops (optionally filtered by search term)
  const fetchLaptops = async (search) => {
    setLaptopLoading(true);
    try {
      // Service returns a shaped laptop[] (empty array when nothing matches)
      const laptopList = await laptopService.getAllLaptops({ search });
      setLaptops(laptopList);
    } catch (error) {
      message.error('Failed to load laptops: ' + error.message);
      setLaptops([]);
    } finally {
      setLaptopLoading(false);
    }
  };

  // Open the add laptop modal
  const handleAddLaptop = () => {
    setEditingLaptop(null);
    laptopForm.resetFields();
    setLaptopModalVisible(true);
  };

  // Open the edit laptop modal
  const handleEditLaptop = (laptop) => {
    setEditingLaptop(laptop);
    laptopForm.setFieldsValue({
      inventoryNumber: laptop.inventoryNumber,
      empId: laptop.empId ?? null,
    });
    setLaptopModalVisible(true);
  };

  // From the employee browse table: open the add-laptop modal with this employee preselected
  const handleAssignLaptopToEmployee = (emp) => {
    setEditingLaptop(null);
    laptopForm.resetFields();
    laptopForm.setFieldsValue({ empId: emp.empId });
    setLaptopModalVisible(true);
  };

  // From the employee browse table: jump straight into editing this employee's laptop
  const handleManageEmployeeLaptop = (emp) => {
    const laptop = laptops.find(l => l.laptopId === emp.laptopId) || {
      laptopId: emp.laptopId,
      inventoryNumber: emp.laptopInventoryNumber,
      empId: emp.empId,
    };
    handleEditLaptop(laptop);
  };

  // Cancel the add/edit laptop modal
  const handleLaptopCancel = () => {
    setLaptopModalVisible(false);
    setEditingLaptop(null);
    laptopForm.resetFields();
  };

  // Create or update a laptop
  const handleSaveLaptop = async (values) => {
    setLaptopSaving(true);
    try {
      // Normalise empId: '' -> null so an empty selection unassigns the owner
      const empId = values.empId === undefined || values.empId === '' ? null : values.empId;
      if (editingLaptop) {
        await laptopService.updateLaptop(editingLaptop.laptopId, {
          inventoryNumber: values.inventoryNumber,
          empId,
        });
        message.success('Laptop updated successfully!');
      } else {
        await laptopService.createLaptop({
          inventoryNumber: values.inventoryNumber,
          empId,
        });
        message.success('Laptop added successfully!');
      }
      setLaptopModalVisible(false);
      setEditingLaptop(null);
      laptopForm.resetFields();
      fetchLaptops(laptopSearch);
      fetchEmployees(); // laptopId/laptopInventoryNumber on employees just changed
    } catch (error) {
      // Map known 409 conflicts to their field; the picker already excludes
      // assigned employees, so the empId case is mainly a race-condition backstop.
      if (error.message.includes('already exists')) {
        laptopForm.setFields([{ name: 'inventoryNumber', errors: [error.message] }]);
      } else if (error.message.includes('already has a laptop')) {
        laptopForm.setFields([{ name: 'empId', errors: [error.message] }]);
      } else {
        message.error('Failed to save laptop: ' + error.message);
      }
    } finally {
      setLaptopSaving(false);
    }
  };

  // Open the delete laptop confirmation
  const handleDeleteLaptop = (laptop) => {
    setLaptopDeleteData(laptop);
    setLaptopDeleteVisible(true);
  };

  // Perform the laptop deletion
  const performDeleteLaptop = async () => {
    if (!laptopDeleteData) return;
    try {
      await laptopService.deleteLaptop(laptopDeleteData.laptopId);
      message.success('Laptop deleted successfully!');
      setLaptopDeleteVisible(false);
      setLaptopDeleteData(null);
      fetchLaptops(laptopSearch);
      fetchEmployees(); // deleted laptop may have freed up its owner
    } catch (error) {
      message.error('Failed to delete laptop: ' + error.message);
    }
  };

  // Email one employee to confirm their laptop's inventory number
  const handleSendConfirmation = async (laptop) => {
    if (!laptop.empId) {
      message.warning('Assign this laptop to an employee before sending a confirmation email.');
      return;
    }
    setLaptopSendingId(laptop.laptopId);
    try {
      const result = await laptopService.sendConfirmation(laptop.laptopId);
      message.success(result.message || 'Confirmation email sent.');
      fetchLaptops(laptopSearch);
    } catch (error) {
      message.error('Failed to send confirmation email: ' + error.message);
    } finally {
      setLaptopSendingId(null);
    }
  };

  // Email every employee with a laptop assigned
  const performSendAllConfirmations = async () => {
    setLaptopSendAllLoading(true);
    try {
      const result = await laptopService.sendConfirmationAll();
      setLaptopSendAllConfirmVisible(false);
      setLaptopSendAllResult(result);
      setLaptopSendAllResultVisible(true);
      fetchLaptops(laptopSearch);
    } catch (error) {
      message.error('Failed to send confirmation emails: ' + error.message);
    } finally {
      setLaptopSendAllLoading(false);
    }
  };

  // ----- Directory: one entry per person -----
  // Employee records (allEmployees) and portal accounts (getAllUsers) are separate lists,
  // joined here by email. People who only exist in one of them are still listed.
  const isOn = (v) => v === 1 || v === '1' || v === true;
  const empName = (e) => `${e.fName || ''} ${e.lName || ''}`.trim();
  const userName = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim();
  const initialsOf = (name) => (name.match(/\b\w/g) || ['?']).slice(0, 2).join('').toUpperCase();

  const people = useMemo(() => {
    const usersByEmail = new Map();
    users.forEach((u) => { if (u.email) usersByEmail.set(u.email.toLowerCase(), u); });
    const laptopsByEmp = new Map();
    laptops.forEach((l) => { if (l.empId) laptopsByEmp.set(l.empId, l); });

    const matched = new Set();
    const list = employees.map((emp) => {
      const email = (emp.empEmail || '').toLowerCase();
      const account = email ? usersByEmail.get(email) : null;
      if (account) matched.add(account.userId);
      const laptop = laptopsByEmp.get(emp.empId)
        || (emp.laptopId ? { laptopId: emp.laptopId, inventoryNumber: emp.laptopInventoryNumber, empId: emp.empId } : null);
      return {
        key: `e-${emp.empId}`,
        name: empName(emp) || emp.empEmail || 'Unnamed',
        email: emp.empEmail,
        employee: emp,
        account,
        laptop,
      };
    });
    users.forEach((u) => {
      if (matched.has(u.userId)) return;
      list.push({ key: `u-${u.userId}`, name: userName(u) || u.email || 'Unnamed', email: u.email, employee: null, account: u, laptop: null });
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, users, laptops]);

  const laptopState = (laptop) => {
    if (!laptop) return null;
    if (laptop.confirmedAt) return { key: 'confirmed', label: 'Confirmed', tone: 'ok' };
    if (laptop.confirmationSentAt) return { key: 'waiting', label: 'Waiting for owner', tone: 'warn' };
    return { key: 'notsent', label: 'Not sent', tone: 'muted' };
  };

  const directory = useMemo(() => {
    const term = peopleSearch.trim().toLowerCase();
    return people.filter((p) => {
      if (term && !`${p.name} ${p.email || ''}`.toLowerCase().includes(term)) return false;
      if (peopleFilter === 'waiting') return p.laptop && !p.laptop.confirmedAt;
      if (peopleFilter === 'nolaptop') return p.employee && !p.laptop;
      return true;
    });
  }, [people, peopleSearch, peopleFilter]);

  const waitingCount = people.filter((p) => p.laptop && !p.laptop.confirmedAt).length;
  const noLaptopCount = people.filter((p) => p.employee && !p.laptop).length;
  const selected = directory.find((p) => p.key === selectedKey) || directory[0] || null;

  // Laptop inventory table columns
  const laptopColumns = [
    {
      title: 'Inventory number',
      dataIndex: 'inventoryNumber',
      key: 'inventoryNumber',
      width: 220,
      render: (value) => (
        <Text strong copyable={value ? { text: value } : false} className="num">{value || 'N/A'}</Text>
      ),
      sorter: (a, b) => `${a.inventoryNumber || ''}`.localeCompare(`${b.inventoryNumber || ''}`),
    },
    {
      title: 'Assigned to',
      key: 'owner',
      render: (_, record) => {
        if (!record.empId) {
          return <span className="emp-state tone-muted">Unassigned</span>;
        }
        return (
          <span className="emp-owner">
            <span className="emp-avatar sm" aria-hidden="true">{initialsOf(record.ownerName || '?')}</span>
            <span>
              <span className="emp-owner__name">{record.ownerName || 'N/A'}</span>
              {record.ownerEmail && <span className="emp-owner__email">{record.ownerEmail}</span>}
            </span>
          </span>
        );
      },
      sorter: (a, b) => `${a.ownerName || ''}`.localeCompare(`${b.ownerName || ''}`),
    },
    {
      title: 'Confirmation',
      key: 'confirmationStatus',
      width: 200,
      render: (_, record) => {
        if (record.confirmedAt) {
          return <span className="emp-state tone-ok" title={`Confirmed ${new Date(record.confirmedAt).toLocaleString()}`}>Confirmed</span>;
        }
        if (record.confirmationSentAt) {
          return <span className="emp-state tone-warn" title={`Sent ${new Date(record.confirmationSentAt).toLocaleString()}`}>Waiting for owner</span>;
        }
        return <span className="emp-state tone-muted">Not sent</span>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 70,
      align: 'right',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'send-confirmation',
            label: 'Send confirmation email',
            icon: <MailOutlined />,
            disabled: !record.empId || laptopSendingId === record.laptopId,
          },
          { key: 'edit', label: 'Edit laptop', icon: <EditOutlined /> },
          { key: 'delete', label: 'Delete laptop', icon: <DeleteOutlined />, danger: true },
        ];
        const handleMenuClick = ({ key }) => {
          if (key === 'edit') {
            handleEditLaptop(record);
          } else if (key === 'delete') {
            handleDeleteLaptop(record);
          } else if (key === 'send-confirmation') {
            handleSendConfirmation(record);
          }
        };
        return (
          <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} aria-label={`Actions for ${record.inventoryNumber}`} onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        );
      },
    },
  ];

  const renderRecord = () => {
    if (!selected) {
      return (
        <div className="emp-record__empty">
          <strong>{people.length ? 'Nobody matches' : 'No employees yet'}</strong>
          <span>{people.length ? 'Try another search or filter.' : 'Add the first employee to get started.'}</span>
        </div>
      );
    }
    const { employee: emp, account, laptop } = selected;
    const state = laptopState(laptop);
    const extraRoles = (account?.additionalRoles || []).filter((r) => r !== account?.role);
    return (
      <>
        <header className="emp-record__head">
          <span className="emp-avatar lg" aria-hidden="true">{initialsOf(selected.name)}</span>
          <div className="emp-record__titles">
            <h2 className="emp-record__name">{selected.name}</h2>
            {selected.email && <Text className="emp-record__email" copyable={{ text: selected.email }}>{selected.email}</Text>}
          </div>
          {emp && (
            <div className="emp-record__actions">
              <Button icon={<EditOutlined />} onClick={() => handleEditEmployee(emp)}>Edit details</Button>
              <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteEmployee(emp)}>Delete</Button>
            </div>
          )}
        </header>

        <section className="emp-sec" aria-labelledby="emp-sec-details">
          <h3 className="emp-sec__title" id="emp-sec-details">Details</h3>
          {emp ? (
            <dl className="emp-kv">
              <div><dt>Hourly rate</dt><dd className="num">€{emp.hourlyRate || '0'}</dd></div>
              <div><dt>Gross wage</dt><dd className="num">€{Number(emp.grossWage || 0).toLocaleString('en-GB')}</dd></div>
              <div>
                <dt>Disturbance allowance</dt>
                <dd>{isOn(emp.DisturbanceAllowance) ? <span className="emp-state tone-ok">On</span> : <span className="emp-state tone-muted">Off</span>}</dd>
              </div>
              <div><dt>First name</dt><dd>{emp.fName || '—'}</dd></div>
              <div><dt>Last name</dt><dd>{emp.lName || '—'}</dd></div>
            </dl>
          ) : (
            <p className="emp-note">This person has a portal account but no employee record, so there are no pay details or laptop to show.</p>
          )}
        </section>

        <section className="emp-sec" aria-labelledby="emp-sec-roles">
          <div className="emp-sec__head">
            <h3 className="emp-sec__title" id="emp-sec-roles">Roles and access</h3>
            {account && <Button size="small" icon={<SafetyCertificateTwoTone twoToneColor="#2748d9" />} onClick={() => handleEditRole(account)}>Change roles</Button>}
          </div>
          {account ? (
            <>
              <p className="emp-note muted">The main role decides what {account.firstName || 'they'} can see. Extra roles add to it.</p>
              <div className="emp-roleline">
                <span className="emp-roleline__label">Main role</span>
                <span className="emp-chip is-main">{getRoleName(account.role)}</span>
              </div>
              <div className="emp-roleline">
                <span className="emp-roleline__label">Extra roles</span>
                {extraRoles.length
                  ? extraRoles.map((r) => <span key={r} className="emp-chip">{getRoleName(r)}</span>)
                  : <span className="muted">None</span>}
              </div>
            </>
          ) : (
            <p className="emp-note">No portal account uses this email yet. Roles can be set once they register.</p>
          )}
        </section>

        {emp && (
          <section className="emp-sec" aria-labelledby="emp-sec-laptop">
            <h3 className="emp-sec__title" id="emp-sec-laptop">Laptop</h3>
            {laptop ? (
              <div className="emp-laptop">
                <span className={`emp-laptop__icon tone-${state.tone}`} aria-hidden="true"><LaptopOutlined /></span>
                <div className="emp-laptop__text">
                  <span className="emp-laptop__number num">{laptop.inventoryNumber}</span>
                  <span className={`emp-state tone-${state.tone}`}>
                    {state.label}
                    {state.key === 'confirmed' && laptop.confirmedAt ? ` ${dayjs(laptop.confirmedAt).format('D MMM YYYY')}` : ''}
                    {state.key === 'waiting' && laptop.confirmationSentAt ? `, email sent ${dayjs(laptop.confirmationSentAt).format('D MMM')}` : ''}
                  </span>
                </div>
                <div className="emp-laptop__actions">
                  {state.key !== 'confirmed' && (
                    <Button
                      size="small"
                      icon={<MailOutlined />}
                      loading={laptopSendingId === laptop.laptopId}
                      onClick={() => handleSendConfirmation(laptop)}
                    >
                      {state.key === 'waiting' ? 'Send again' : 'Send confirmation'}
                    </Button>
                  )}
                  <Button size="small" icon={<EditOutlined />} onClick={() => handleManageEmployeeLaptop(emp)}>Edit</Button>
                </div>
              </div>
            ) : (
              <div className="emp-laptop is-empty">
                <span className="emp-laptop__icon tone-muted" aria-hidden="true"><LaptopOutlined /></span>
                <span className="emp-laptop__text muted">No laptop assigned.</span>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => handleAssignLaptopToEmployee(emp)}>Assign laptop</Button>
              </div>
            )}
          </section>
        )}
      </>
    );
  };

  const filters = [
    { key: 'all', label: 'Everyone', count: people.length },
    { key: 'waiting', label: 'Laptop waiting', count: waitingCount },
    { key: 'nolaptop', label: 'No laptop', count: noLaptopCount },
  ];

  return (
    <DOTheme>
      <Layout className="do-shell do-scope emp-page">
        <NavigationBar />
        <main className="do-main emp-main">
          <aside className="emp-side">
            <div className="emp-side__head">
              <h1 className="emp-title">Employees</h1>
              <Segmented
                block
                value={activeTab}
                onChange={setActiveTab}
                options={[
                  { value: 'employees', label: <span className="emp-seg">People <em>{people.length}</em></span> },
                  { value: 'laptops', label: <span className="emp-seg">Laptops <em>{laptops.length}</em></span> },
                ]}
                aria-label="Show"
              />
            </div>

            {activeTab === 'employees' ? (
              <>
                <div className="emp-side__tools">
                  <Input
                    allowClear
                    prefix={<SearchOutlined aria-hidden="true" />}
                    placeholder="Search name or email"
                    value={peopleSearch}
                    onChange={(e) => setPeopleSearch(e.target.value)}
                    aria-label="Search people"
                  />
                  <Tooltip title="Refresh">
                    <Button icon={<ReloadOutlined />} onClick={() => { fetchEmployees(); fetchUsers(); fetchLaptops(laptopSearch); }} loading={loading} aria-label="Refresh" />
                  </Tooltip>
                </div>
                <div className="emp-filters" role="group" aria-label="Filter people">
                  {filters.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={`emp-filter${peopleFilter === f.key ? ' is-on' : ''}`}
                      aria-pressed={peopleFilter === f.key}
                      onClick={() => setPeopleFilter(f.key)}
                    >
                      {f.label} <em className="num">{f.count}</em>
                    </button>
                  ))}
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)} block className="emp-add">
                  Add employee
                </Button>
                {loading && people.length === 0 ? (
                  <div className="emp-list__state"><Spin /></div>
                ) : (
                  <ul className="emp-list" aria-label="People">
                    {directory.map((p) => {
                      const st = laptopState(p.laptop);
                      const role = p.account ? getRoleName(p.account.role) : 'No portal account';
                      return (
                        <li key={p.key}>
                          <button
                            type="button"
                            className={`emp-person${selected?.key === p.key ? ' is-on' : ''}`}
                            aria-current={selected?.key === p.key ? 'true' : undefined}
                            onClick={() => setSelectedKey(p.key)}
                          >
                            <span className="emp-avatar" aria-hidden="true">{initialsOf(p.name)}</span>
                            <span className="emp-person__text">
                              <span className="emp-person__name">{p.name}</span>
                              <span className="emp-person__role">{role}</span>
                            </span>
                            {st && st.key !== 'confirmed' && <i className={`emp-dot tone-${st.tone}`} title={`Laptop: ${st.label}`} />}
                          </button>
                        </li>
                      );
                    })}
                    {directory.length === 0 && <li className="emp-list__state">Nobody matches.</li>}
                  </ul>
                )}
              </>
            ) : (
              <div className="emp-side__laptops">
                <dl className="emp-figs">
                  <div><dt>Laptops</dt><dd className="num">{laptops.length}</dd></div>
                  <div><dt>Assigned</dt><dd className="num">{laptops.filter(l => l.empId).length}</dd></div>
                  <div><dt>Unassigned</dt><dd className="num">{laptops.filter(l => !l.empId).length}</dd></div>
                  <div><dt>Confirmed</dt><dd className="num">{laptops.filter(l => l.confirmedAt).length}</dd></div>
                </dl>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddLaptop} block>Add laptop</Button>
                <Button
                  icon={<SendOutlined />}
                  onClick={() => setLaptopSendAllConfirmVisible(true)}
                  disabled={!laptops.some(l => l.empId)}
                  block
                >
                  Send all confirmations
                </Button>
                <p className="emp-note muted">Owners confirm their inventory number from My account.</p>
              </div>
            )}
          </aside>

          <section className="emp-sheet" aria-live="polite">
            {activeTab === 'employees' ? (
              <div className="emp-record">{renderRecord()}</div>
            ) : (
              <>
                <header className="emp-sheet__head">
                  <div>
                    <h2 className="emp-sheet__title">Laptops</h2>
                    <p className="emp-sheet__sub">Every laptop, who has it, and whether they've confirmed the number.</p>
                  </div>
                  <div className="emp-sheet__tools">
                    <Input.Search
                      placeholder="Search inventory number or owner"
                      allowClear
                      value={laptopSearch}
                      onChange={(e) => setLaptopSearch(e.target.value)}
                      onSearch={(value) => fetchLaptops(value)}
                      style={{ width: 300 }}
                      aria-label="Search laptops"
                    />
                    <Tooltip title="Refresh">
                      <Button icon={<ReloadOutlined />} onClick={() => fetchLaptops(laptopSearch)} loading={laptopLoading} aria-label="Refresh laptops" />
                    </Tooltip>
                  </div>
                </header>
                <Table
                  className="emp-table"
                  columns={laptopColumns}
                  dataSource={laptops}
                  loading={laptopLoading}
                  rowKey="laptopId"
                  scroll={{ x: 720 }}
                  pagination={{
                    pageSize: 20,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} laptops`,
                  }}
                  locale={{ emptyText: 'No laptops found' }}
                />
              </>
            )}
          </section>

        {/* Edit Role Modal */}
        <Modal
          title="Edit User Role"
          open={roleModalVisible}
          onCancel={() => {
            setRoleModalVisible(false);
            roleForm.resetFields();
            setEditingUser(null);
            setEditingUserAdditionalRoles([]);
            setAdditionalRoleToAdd(null);
          }}
          footer={null}
          width={window.innerWidth <= 768 ? '90%' : 600}
          destroyOnClose
        >
          <Form
            form={roleForm}
            layout="vertical"
            onFinish={handleUpdateRole}
          >
            {editingUser && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                <Text><strong>User:</strong> {editingUser.firstName} {editingUser.lastName}</Text><br/>
                <Text type="secondary"><strong>Email:</strong> {editingUser.email}</Text>
              </div>
            )}

            <Form.Item
              label="Primary Role"
              name="role"
              rules={[{ required: true, message: 'Please select a role!' }]}
            >
              <Select 
                placeholder="Select a role"
                size="large"
              >
                {roles.map(role => (
                  <Select.Option key={role.role} value={role.role}>
                    <Space>
                      <SafetyOutlined />
                      <span><strong>{role.name}</strong> - {role.description}</span>
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Divider orientation="left" style={{ marginTop: 8 }}>Additional Roles</Divider>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Additional roles grant extra tab access on top of the primary role.
              </Text>
              <div style={{ marginTop: 8, minHeight: 32 }}>
                {editingUserAdditionalRoles.length === 0 ? (
                  <Text type="secondary">No additional roles assigned.</Text>
                ) : (
                  <Space wrap>
                    {editingUserAdditionalRoles.map(roleId => (
                      <Tag
                        key={roleId}
                        color="purple"
                        icon={<SafetyOutlined />}
                        closable
                        onClose={(e) => { e.preventDefault(); handleRemoveAdditionalRole(roleId); }}
                      >
                        {getRoleName(roleId)}
                      </Tag>
                    ))}
                  </Space>
                )}
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <Select
                  placeholder="Select a role to add..."
                  value={additionalRoleToAdd}
                  onChange={setAdditionalRoleToAdd}
                  style={{ flex: 1 }}
                >
                  {roles
                    .filter(r => !editingUserAdditionalRoles.includes(r.role))
                    .map(role => (
                      <Select.Option key={role.role} value={role.role}>
                        {role.name}
                      </Select.Option>
                    ))}
                </Select>
                <Button
                  type="primary"
                  htmlType="button"
                  icon={<PlusOutlined />}
                  onClick={handleAddAdditionalRole}
                  loading={addingAdditionalRole}
                  disabled={!additionalRoleToAdd}
                >
                  Add
                </Button>
              </div>
            </div>

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setRoleModalVisible(false);
                  roleForm.resetFields();
                  setEditingUser(null);
                  setEditingUserAdditionalRoles([]);
                  setAdditionalRoleToAdd(null);
                }}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={roleLoading}
                  icon={<EditOutlined />}
                >
                  Update Role
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Add Employee Modal */}
        <Modal
          title="Add New Employee"
          open={addModalVisible}
          onCancel={handleCancelAdd}
          footer={null}
          width={window.innerWidth <= 768 ? '90%' : 600}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddEmployee}
            requiredMark="optional"
          >
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', 
              gap: '16px' 
            }}>
              <Form.Item
                name="fName"
                label="First Name"
                rules={[
                  { required: true, message: 'First name is required' },
                  { min: 2, message: 'First name must be at least 2 characters' }
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>

              <Form.Item
                name="lName"
                label="Last Name"
                rules={[
                  { required: true, message: 'Last name is required' },
                  { min: 2, message: 'Last name must be at least 2 characters' }
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </div>

            <Form.Item
              name="empEmail"
              label="Email Address"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item
              name="DisturbanceAllowance"
              label="Disturbance Allowance"
              valuePropName="checked"
              extra="Enable disturbance allowance for this employee"
            >
              <Switch 
                checkedChildren="Enabled" 
                unCheckedChildren="Disabled" 
              />
            </Form.Item>

            <Form.Item
              name="hourlyRate"
              label="Hourly Rate"
            >
              <Input 
                type="number" 
                min="0" 
                step="0.5" 
                placeholder="Enter hourly rate" 
              />
            </Form.Item>

            <Form.Item
              name="grossWage"
              label="Gross Wage"
            >
              <Input 
                type="number" 
                min="0" 
                step="500" 
                placeholder="Enter gross wage" 
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={handleCancelAdd}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={addEmployeeLoading}
                  icon={<PlusOutlined />}
                >
                  Add Employee
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit Employee Modal */}
        <Modal
          title={`Edit Employee: ${editingEmployee ? `${editingEmployee.fName} ${editingEmployee.lName}` : ''}`}
          open={editModalVisible}
          onCancel={handleCancelEdit}
          footer={null}
          width={window.innerWidth <= 768 ? '90%' : 600}
          destroyOnClose
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleUpdateEmployee}
            requiredMark="optional"
          >
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', 
              gap: '16px' 
            }}>
              <Form.Item
                name="fName"
                label="First Name"
                rules={[
                  { required: true, message: 'First name is required' },
                  { min: 2, message: 'First name must be at least 2 characters' }
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>

              <Form.Item
                name="lName"
                label="Last Name"
                rules={[
                  { required: true, message: 'Last name is required' },
                  { min: 2, message: 'Last name must be at least 2 characters' }
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </div>

            <Form.Item
              name="empEmail"
              label="Email Address"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item
              name="DisturbanceAllowance"
              label="Disturbance Allowance"
              valuePropName="checked"
              extra="Enable disturbance allowance for this employee"
            >
              <Switch 
                checkedChildren="Enabled" 
                unCheckedChildren="Disabled" 
              />
            </Form.Item>

            <Form.Item
              name="hourlyRate"
              label="Hourly Rate"
            >
              <Input 
                type="number" 
                min="0" 
                step="0.5" 
                placeholder="Enter hourly rate" 
              />
            </Form.Item>

            <Form.Item
              name="grossWage"
              label="Gross Wage"
            >
              <Input 
                type="number" 
                min="0" 
                step="500" 
                placeholder="Enter gross wage" 
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={editEmployeeLoading}
                  icon={<EditOutlined />}
                >
                  Update Employee
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Add / Edit Laptop Modal */}
        <Modal
          title={editingLaptop ? `Edit Laptop: ${editingLaptop.inventoryNumber}` : 'Add New Laptop'}
          open={laptopModalVisible}
          onCancel={handleLaptopCancel}
          footer={null}
          width={window.innerWidth <= 768 ? '90%' : 600}
          destroyOnClose
        >
          <Form
            form={laptopForm}
            layout="vertical"
            onFinish={handleSaveLaptop}
            requiredMark="optional"
          >
            <Form.Item
              name="inventoryNumber"
              label="Inventory Number"
              rules={[{ required: true, message: 'Inventory number is required' }]}
            >
              <Input placeholder="Enter inventory number" />
            </Form.Item>

            <Form.Item
              name="empId"
              label="Assigned To"
              extra="Leave empty to keep the laptop unassigned. Employees who already own a laptop aren't listed."
            >
              <Select
                placeholder="Select an employee (optional)"
                allowClear
                showSearch
                optionFilterProp="label"
                // Only offer employees without a laptop, plus this laptop's
                // current owner (if any) so re-saving the same owner works.
                options={employees
                  .filter(emp => !emp.laptopId || (editingLaptop && emp.laptopId === editingLaptop.laptopId))
                  .map(emp => ({
                    value: emp.empId,
                    label: `${emp.fName || ''} ${emp.lName || ''}`.trim() + (emp.empEmail ? ` (${emp.empEmail})` : ''),
                  }))}
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={handleLaptopCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={laptopSaving}
                  icon={editingLaptop ? <EditOutlined /> : <PlusOutlined />}
                >
                  {editingLaptop ? 'Update Laptop' : 'Add Laptop'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Delete Laptop Confirmation Modal */}
        <Modal
          title="Delete Laptop"
          open={laptopDeleteVisible}
          onCancel={() => { setLaptopDeleteVisible(false); setLaptopDeleteData(null); }}
          footer={[
            <Button key="cancel" onClick={() => { setLaptopDeleteVisible(false); setLaptopDeleteData(null); }}>
              Cancel
            </Button>,
            <Button key="ok" type="primary" danger onClick={performDeleteLaptop}>
              Delete
            </Button>,
          ]}
          width={window.innerWidth <= 768 ? '90%' : 500}
          destroyOnClose
        >
          {laptopDeleteData && (
            <div style={{ padding: '16px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '22px', marginRight: '12px' }} />
                <span style={{ fontSize: '16px', fontWeight: 500 }}>Confirm Deletion</span>
              </div>
              <p>Are you sure you want to delete laptop:</p>
              <p><strong>{laptopDeleteData.inventoryNumber}</strong></p>
              {laptopDeleteData.ownerName && (
                <p>Currently assigned to: <strong>{laptopDeleteData.ownerName}</strong></p>
              )}
            </div>
          )}
        </Modal>

        {/* Send All Confirmations — confirm modal */}
        <Modal
          title="Send All Confirmation Emails"
          open={laptopSendAllConfirmVisible}
          onCancel={() => setLaptopSendAllConfirmVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setLaptopSendAllConfirmVisible(false)}>
              Cancel
            </Button>,
            <Button key="ok" type="primary" icon={<SendOutlined />} loading={laptopSendAllLoading} onClick={performSendAllConfirmations}>
              Send Emails
            </Button>,
          ]}
          width={window.innerWidth <= 768 ? '90%' : 500}
          destroyOnClose
        >
          <p>This will email every employee who currently has a laptop assigned, asking them to confirm their inventory number is correct.</p>
          <p>Employees who already confirmed will be re-sent a new confirmation email. Continue?</p>
        </Modal>

        {/* Send All Confirmations — result modal */}
        <Modal
          title="Confirmation Emails Sent"
          open={laptopSendAllResultVisible}
          onCancel={() => { setLaptopSendAllResultVisible(false); setLaptopSendAllResult(null); }}
          footer={[
            <Button key="close" type="primary" onClick={() => { setLaptopSendAllResultVisible(false); setLaptopSendAllResult(null); }}>
              Close
            </Button>,
          ]}
          width={window.innerWidth <= 768 ? '90%' : 500}
          destroyOnClose
        >
          {laptopSendAllResult && (
            <div>
              <p>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                Sent: <strong>{laptopSendAllResult.sentCount ?? 0}</strong>
              </p>
              {laptopSendAllResult.failedCount > 0 && (
                <>
                  <p>
                    <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                    Failed: <strong>{laptopSendAllResult.failedCount}</strong>
                  </p>
                  <ul style={{ paddingLeft: 20 }}>
                    {(laptopSendAllResult.failed || []).map((f, idx) => (
                      <li key={idx}>
                        Employee {f.empId}{f.empEmail ? ` (${f.empEmail})` : ''}: {f.error}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          title={deleteStep === 1 ? 'Delete Employee' : 'Final Confirmation'}
          open={deleteModalVisible}
          onCancel={handleDeleteCancel}
          footer={[
            <Button key="cancel" onClick={handleDeleteCancel}>
              Cancel
            </Button>,
            <Button 
              key="ok" 
              type="primary" 
              danger 
              onClick={deleteStep === 1 ? handleFirstConfirmationOk : handleSecondConfirmationOk}
            >
              {deleteStep === 1 ? 'Yes, Continue' : 'Delete Permanently'}
            </Button>,
          ]}
          width={window.innerWidth <= 768 ? '90%' : 500}
          destroyOnClose
        >
          {deleteEmployeeData && (
            <div style={{ padding: '16px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '22px', marginRight: '12px' }} />
                <span style={{ fontSize: '16px', fontWeight: 500 }}>
                  {deleteStep === 1 ? 'Confirm Deletion' : 'Final Warning'}
                </span>
              </div>
              
              {deleteStep === 1 ? (
                <div>
                  <p>Are you sure you want to delete employee:</p>
                  <p><strong>{deleteEmployeeData.fName} {deleteEmployeeData.lName}</strong></p>
                  <p>Email: <strong>{deleteEmployeeData.empEmail}</strong></p>
                </div>
              ) : (
                <div>
                  <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>This action cannot be undone!</p>
                  <p>Are you absolutely sure you want to permanently delete:</p>
                  <p><strong>{deleteEmployeeData.fName} {deleteEmployeeData.lName}</strong> ({deleteEmployeeData.empEmail})?</p>
                </div>
              )}
            </div>
          )}
        </Modal>
        </main>
      </Layout>
    </DOTheme>
  );
};

export default EmployeeAdmin;